import type {
  ApolloCache,
  ApolloClient,
  MutationUpdaterFn,
} from '@apollo/client';
import type { Zone } from '@seek/audience-zones';
import type { Language } from '@seek/ca-myprofile-utils';

import { getDataFromCache, updateCache } from 'src/modules/apollo/apolloCache';
import { mergeItemIntoListById } from 'src/modules/utils';
import * as Schema from 'src/types/graphql';

export const getLicenceDataFromStore = <T extends Record<string, any>>({
  cache,
  languageCode,
  zone,
}: {
  cache: ApolloCache<T>;
  languageCode: Language;
  zone: Zone;
}) => {
  const data = getDataFromCache({
    cache,
    query: Schema.GetLicencesDocument,
    variables: { languageCode, zone },
  });

  return data?.viewer?.licences || [];
};

export const updateCacheUpdateLicence: MutationUpdaterFn<
  Schema.UpdateLicenceMutation
> = (cache, { data }) => {
  const { updateLicence } = data || {};

  // Refresh store with server data
  if (updateLicence?.viewer?.licences) {
    const { licences } = updateLicence.viewer;
    updateCache({ cache, data: licences, fieldName: 'licences' });
  }
};

export const updateCacheUpdateVerifiedLicence =
  (
    languageCode: Language,
    zone: Zone,
  ): MutationUpdaterFn<Schema.UpdateVerifiedLicenceMutation> =>
  (cache, { data }) => {
    const { updateVerifiedLicence } = data || {};
    const licences = getLicenceDataFromStore({ cache, languageCode, zone });
    const licenceToUpdate = licences.find(
      (licence) => licence.id === updateVerifiedLicence?.id,
    );

    if (licenceToUpdate) {
      cache.modify({
        id: cache.identify(licenceToUpdate),
        fields: {
          description() {
            return updateVerifiedLicence?.description || null;
          },
        },
      });
    }
  };

export const updateCacheDeleteLicence: MutationUpdaterFn<
  Schema.DeleteLicenceMutation
> = (cache, { data }) => {
  const { deleteLicence } = data || {};

  // Refresh store with server data
  if (deleteLicence?.viewer?.licences) {
    const { licences } = deleteLicence.viewer;
    updateCache({ cache, data: licences, fieldName: 'licences' });
  }
};

export const generateUpdateLicenceOptimisticResponse =
  ({
    _id,
    languageCode,
    zone,
    apolloClient,
  }: {
    _id: string;
    languageCode: Language;
    zone: Zone;
    apolloClient: ApolloClient<any>;
  }) =>
  ({
    licenceData: {
      id,
      name,
      issuingOrganisation,
      issueDate,
      noExpiryDate,
      expiryDate,
      description,
    },
  }: Schema.UpdateLicenceMutationVariables) => ({
    __typename: 'Mutation',
    updateLicence: {
      __typename: 'UpdateLicencePayload',
      licence: {
        __typename: 'Licence',
        id,
      },
      viewer: {
        __typename: 'Candidate',
        _id,
        licences: [
          ...mergeItemIntoListById({
            list: getLicenceDataFromStore({
              ...apolloClient,
              languageCode,
              zone,
            }),
            item: {
              __typename: 'Licence',
              id,
              name: {
                ...name,
                __typename: 'OntologyStructuredData',
              },
              issuingOrganisation,
              issueDate: issueDate
                ? { __typename: 'MonthYear', ...issueDate }
                : null,
              expiryDate: expiryDate
                ? { __typename: 'MonthYear', ...expiryDate }
                : null,
              noExpiryDate,
              description,
              status: null,
              formattedDate: null,
              verificationUrl: null,
              verificationMessage: null,
              credentialType: null,
              credential: null,
            },
            addToFrontOfList: true,
          }),
        ],
      },
    },
  });

export const generateDeleteLicenceOptimisticResponse =
  ({
    apolloClient,
    _id,
    languageCode,
    zone,
  }: {
    apolloClient: ApolloClient<any>;
    _id: string;
    languageCode: Language;
    zone: Zone;
  }) =>
  ({ deleteLicenceInput }: Schema.DeleteLicenceMutationVariables) => {
    const licences = getLicenceDataFromStore({
      ...apolloClient,
      languageCode,
      zone,
    });

    return {
      __typename: 'Mutation',
      deleteLicence: {
        __typename: 'DeleteLicencePayload',
        viewer: {
          __typename: 'Candidate',
          _id,
          licences: licences.filter(
            (licence) => licence.id !== deleteLicenceInput.id,
          ),
        },
      },
    };
  };
